import { z } from 'zod';

import { CITY_LAUNCH_STEPS, type CityLaunchStep } from '../constants/cityLaunch';
import { IdSchema } from './common';

/**
 * Мультигород (BLUEPRINT Stage 14, задача 1): заведение города с районами и часовым поясом,
 * назначение оператора города и чек-лист открытия набора.
 */

export const CitySlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9-]{1,39}$/, 'Код города: латиница, цифры и дефис');

/** IANA-таймзона: проверяем формат, существование — на сервере через Intl. */
export const TimezoneSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z]+\/[A-Za-z0-9_+-]+(\/[A-Za-z0-9_+-]+)?$/, 'Таймзона в формате Europe/Moscow');

export const DistrictInputSchema = z.object({
  /** Пусто — новый район */
  id: IdSchema.optional(),
  name: z.string().trim().min(2, 'Название района от 2 символов').max(80),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9-]{1,39}$/, 'Код района: латиница, цифры и дефис'),
  lat: z.number().min(-90).max(90).nullable().default(null),
  lng: z.number().min(-180).max(180).nullable().default(null),
  /** Коды соседних районов этого же города */
  neighbors: z.array(z.string().trim().toLowerCase()).max(20).default([]),
});
export type DistrictInput = z.infer<typeof DistrictInputSchema>;

export const AdminCityCreateInputSchema = z.object({
  name: z.string().trim().min(2, 'Название города от 2 символов').max(80),
  slug: CitySlugSchema,
  timezone: TimezoneSchema,
  districts: z.array(DistrictInputSchema).min(1, 'Заведи хотя бы один район').max(60),
});
export type AdminCityCreateInput = z.infer<typeof AdminCityCreateInputSchema>;

export const AdminCityUpdateInputSchema = z.object({
  cityId: IdSchema,
  name: AdminCityCreateInputSchema.shape.name.optional(),
  timezone: TimezoneSchema.optional(),
  isActive: z.boolean().optional(),
  /** Полный список районов города: чего нет в списке — удаляется, если не использовано */
  districts: z.array(DistrictInputSchema).max(60).optional(),
});
export type AdminCityUpdateInput = z.infer<typeof AdminCityUpdateInputSchema>;

export const AdminCityIdInputSchema = z.object({ cityId: IdSchema });

/** Назначение оператора города: роль OPS и список городов (пусто — все города). */
export const AdminCityOperatorInputSchema = z.object({
  userId: IdSchema,
  cityIds: z.array(IdSchema).max(20),
});
export type AdminCityOperatorInput = z.infer<typeof AdminCityOperatorInputSchema>;

// ───────────────────────────── чек-лист открытия ─────────────────────────────

export const CityLaunchStepSchema = z.enum(CITY_LAUNCH_STEPS as unknown as [CityLaunchStep, ...CityLaunchStep[]]);

export const CityLaunchItemSchema = z.object({
  step: CityLaunchStepSchema,
  ok: z.boolean(),
  /** Предупреждение, а не блок: открыть город можно и с невыполненным пунктом */
  soft: z.boolean(),
  /** Сколько есть */
  have: z.number().int().nonnegative(),
  /** Сколько нужно */
  need: z.number().int().nonnegative(),
  /** Уточнение под пунктом: «падел, мини-футбол», «Москва: 3 отряда» */
  detail: z.string().nullable(),
});
export type CityLaunchItem = z.infer<typeof CityLaunchItemSchema>;

export const CityLaunchChecklistSchema = z.object({
  cityId: IdSchema,
  cityName: z.string(),
  /** Город уже открыт для набора */
  opened: z.boolean(),
  openedAt: z.string().nullable(),
  items: z.array(CityLaunchItemSchema),
  /** Все жёсткие пункты выполнены — кнопка «Открыть город» активна */
  canOpen: z.boolean(),
});
export type CityLaunchChecklist = z.infer<typeof CityLaunchChecklistSchema>;

// ───────────────────────────── строка списка городов ─────────────────────────────

export const AdminCityDistrictSchema = z.object({
  id: IdSchema,
  name: z.string(),
  slug: z.string(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  neighbors: z.array(z.string()),
  /** Заявок пула, где район выбран — район с заявками не удаляем */
  applications: z.number().int().nonnegative(),
});
export type AdminCityDistrict = z.infer<typeof AdminCityDistrictSchema>;

export const AdminCityRowSchema = z.object({
  id: IdSchema,
  name: z.string(),
  slug: z.string(),
  timezone: z.string(),
  isActive: z.boolean(),
  openedAt: z.string().nullable(),
  districts: z.array(AdminCityDistrictSchema),
  venues: z.number().int().nonnegative(),
  squads: z.number().int().nonnegative(),
  poolWaiting: z.number().int().nonnegative(),
  members: z.number().int().nonnegative(),
  operators: z.array(z.object({ id: IdSchema, name: z.string() })),
});
export type AdminCityRow = z.infer<typeof AdminCityRowSchema>;
